import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import {useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';
import {SidebarLinkClients} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {
  SiteCoordinatesViewMeta,
  SiteDocumentTitle,
  SiteViewGeneralMeta,
} from 'src/pages/Modules/Clients/shared/siteHelpers';
import {ContactsViewTable} from 'src/pages/Modules/Clients/shared/ContactsFields';
import {EntityActiveStatusPanel} from 'src/pages/Modules/Clients/shared/EntityActiveStatusPanel';
import {DeviceTable} from 'src/pages/Modules/Inspections/shared/DeviceTable';
import {InspectionSchedulesSection} from 'src/pages/Modules/Clients/SiteView/components/InspectionSchedulesSection';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  useDeleteSiteMutation,
  useGetSiteQuery,
  useUpdateSiteActiveStatusMutation,
} from 'src/redux/queries/site';
import {useClientPermissions} from 'src/shared/auth/permissions/useClientPermissions';
import {useInspectionPermissions} from 'src/shared/auth/permissions/useInspectionPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useActiveFeatureFlags from 'src/shared/hooks/useActiveFeatureFlags';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormDataV2} from 'src/types/FormV2';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const SiteViewPage = () => {
  const navigate = useAppNavigate();
  const {siteId} = useParams();
  const {setAlert} = useAlert();
  const [isDeleting, setIsDeleting] = useState(false);
  const clientPerms = useClientPermissions();
  const inspectionPerms = useInspectionPermissions();
  const activeFeatureFlags = useActiveFeatureFlags();
  const inspectionsEnabled =
    activeFeatureFlags?.includes(FeatureFlagEnum.Inspections) === true;
  const showInspectionSections = inspectionsEnabled && inspectionPerms.view;
  useSetDocumentTitle(SiteDocumentTitle);

  const validSiteId =
    siteId !== undefined && !isNaN(+siteId) ? +siteId : undefined;

  const {data: viewItem, isLoading} = useGetSiteQuery(validSiteId!, {
    skip: !validSiteId,
  });

  const [deleteSite] = useDeleteSiteMutation();
  const [updateSiteActiveStatus, {isLoading: isTogglingActive}] =
    useUpdateSiteActiveStatusMutation();

  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);

  const handleDelete = async () => {
    confirmDeleteHandlers.close();

    try {
      if (!siteId) {
        setAlert('ID not found.', 'error');
        return;
      }

      setIsDeleting(true);

      await deleteSite(+siteId).unwrap();

      setAlert('Site was successfully deleted.', 'success');

      navigate(PathUtility.Clients('sites'));
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsDeleting(false);
  };

  if (isLoading) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Clients}>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </ContentWrapper>
    );
  }

  if (!viewItem) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Clients}>
        <Alert color="red" title="Not Found">
          Site not found.
        </Alert>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Clients}>
      <Stack gap="lg">
        <BackBar
          fallbackTo={PathUtility.Clients('sites')}
          fallbackLabel="Back to Sites"
          breadcrumbs={[
            {label: 'Sites', to: PathUtility.Clients('sites')},
            {label: viewItem.name ?? 'Site'},
          ]}
        />

        <PageHeader>
          <PageHeader.Title
            icon={SidebarLinkClients.icon}
            subtitle={viewItem.siteNumber}>
            {viewItem.name ?? 'Site'}
          </PageHeader.Title>

          <PageHeader.Badges>
            <PageHeader.Badge
              color={viewItem.active ? 'green' : 'yellow'}
              variant="light">
              {viewItem.active ? 'Active' : 'Inactive'}
            </PageHeader.Badge>
          </PageHeader.Badges>

          <PageHeader.Actions>
            {clientPerms.update && (
              <PageHeader.Action
                variant="edit"
                onClick={() => navigate(PathUtility.SitesEdit(siteId!))}>
                Edit
              </PageHeader.Action>
            )}
            {clientPerms.delete && (
              <PageHeader.Action
                variant="delete"
                onClick={() => confirmDeleteHandlers.open()}
                loading={isDeleting}>
                Delete
              </PageHeader.Action>
            )}
          </PageHeader.Actions>
        </PageHeader>

        <SectionCard title="Client Association">
          <Group p="md">
            {viewItem.clientId ? (
              <Anchor
                component={Link}
                to={PathUtility.ClientsView(viewItem.clientId)}>
                {viewItem.clientName ?? 'View Client'}
              </Anchor>
            ) : (
              <Text size="sm" c="dimmed">
                This site is not associated with a client.
              </Text>
            )}
          </Group>
        </SectionCard>

        <FormViewComponentV2
          formData={viewItem as IFormDataV2}
          formMeta={SiteViewGeneralMeta}
        />

        {(viewItem.latitude !== undefined ||
          viewItem.longitude !== undefined) && (
          <FormViewComponentV2
            formData={viewItem as IFormDataV2}
            formMeta={SiteCoordinatesViewMeta}
          />
        )}

        <ContactsViewTable contacts={viewItem.contacts} />

        {showInspectionSections && (
          <SectionCard title="Devices">
            <DeviceTable
              siteId={viewItem.id}
              identifier={`device-for-site-${viewItem.id}`}
              showHelp={false}
            />
          </SectionCard>
        )}

        {showInspectionSections && (
          <InspectionSchedulesSection
            siteId={viewItem.id}
            siteTimeZone={viewItem.timeZone ?? undefined}
            canManage={inspectionPerms.manageSchedules}
            canUpdateSite={clientPerms.update}
          />
        )}

        {clientPerms.update && (
          <EntityActiveStatusPanel
            entityLabel="Site"
            isActive={!!viewItem.active}
            loading={isTogglingActive}
            onToggle={() =>
              updateSiteActiveStatus({
                id: viewItem.id,
                active: !viewItem.active,
              }).unwrap()
            }
          />
        )}
      </Stack>

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        title="Are you sure you want to delete this site?"
        body="This action cannot be undone."
      />
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.SitesView(':siteId'),
  element: <SiteViewPage />,
  module: ModuleEnum.Clients,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});
